import axios from "axios";
import useUserStore from "@store/useUserStore";

const isNative = () => {
    if (typeof window === "undefined") return false;
    // Android WebView bridge
    if (window.Android) return true;
    // iOS WebView bridge (high-level or low-level)
    if (window.nativeApp) return true;
    if (window.webkit?.messageHandlers?.nativeApp) return true;
    return false;
};

const isAppWebView = () => {
    if (typeof window === "undefined") return false;
    // Android WebView bridge
    if (window.Android) return true;
    // iOS WebView bridge
    if (window.nativeApp) return true;
    if (window.webkit?.messageHandlers?.nativeApp) return true;
    return false;
};

const syncNativeAccessToken = (accessToken) => {
    if (!accessToken || typeof window === "undefined") return;
    try {
        if (window?.Android?.setAuthToken) {
            window.Android.setAuthToken(accessToken);
        }
        if (window?.nativeApp?.setAuthToken) {
            window.nativeApp.setAuthToken(accessToken);
        } else if (window?.webkit?.messageHandlers?.nativeApp) {
            window.webkit.messageHandlers.nativeApp.postMessage({
                action: "setAuthToken",
                token: accessToken,
            });
        }
    } catch (error) {
        if (process.env.NODE_ENV !== "production") {
            console.error("네이티브 앱 토큰 동기화 실패:", error);
        }
    }
};

const syncNativeRefreshCookie = async () => {
    if (!isAppWebView()) return;
    try {
        if (window.Android?.syncRefreshTokenFromCookie) {
            window.Android.syncRefreshTokenFromCookie();
        } else if (window.nativeApp?.syncRefreshTokenFromCookie) {
            window.nativeApp.syncRefreshTokenFromCookie();
        } else if (window.webkit?.messageHandlers?.nativeApp) {
            window.webkit.messageHandlers.nativeApp.postMessage({
                action: "syncRefreshTokenFromCookie",
            });
        }
    } catch (e) {
        // native bridge 실패는 무시
    }
};

// 푸시 목적지 처리 결과를 네이티브에 통보한다.
// 네이티브는 routed/dropped/invalid 에서만 목적지를 terminal 소비하고, pending 이면 보존해 재전달한다.
const PUSH_ROUTE_ACK_STATUSES = ["routed", "pending", "dropped", "invalid"];

// FE·AOS·iOS 공통 ACK 계약의 판별 키. Android WebAppInterface.pushRouteAck 는
// action 이 이 값이 아니면 payload 를 통째로 버린다("unexpected action").
const PUSH_ROUTE_ACK_ACTION = "pushRouteAck";
// iOS PushRouteAck.maxFieldLength 와 같은 상한. 넘는 값은 네이티브가 필드를 거부하므로
// 잘라서 보내지 않고(경로가 조용히 바뀐다) 아예 싣지 않는다.
const PUSH_ROUTE_ACK_MAX_FIELD_LENGTH = 2048;

// 네이티브 계약 필드는 비어 있지 않은 문자열이어야 한다(iOS 는 String 캐스팅 실패 시 폐기).
// 숫자 id 가 그대로 넘어오는 경로가 있어 문자열로 정규화한다.
const normalizeAckField = (value) => {
    if (value === undefined || value === null) return null;
    const text = String(value).trim();
    if (!text || text.length > PUSH_ROUTE_ACK_MAX_FIELD_LENGTH) return null;
    return text;
};

const sendPushRouteAck = ({ status, path, pushId } = {}) => {
    if (typeof window === "undefined") return;
    // 계약에 없는 status 는 네이티브 소비 판단을 오염시키므로 보내지 않는다.
    if (!PUSH_ROUTE_ACK_STATUSES.includes(status)) return;

    const notificationId = normalizeAckField(pushId);
    const actionUrl = normalizeAckField(path);

    // 하위호환 키(status/path/pushId)와 네이티브 계약 키(action/notification_id/action_url)를
    // 한 payload 에 함께 싣는다. 둘 중 하나만 보내면 한 플랫폼이 조용히 ACK 를 버린다 —
    // Android 는 action 이 없으면 무시하고, iOS 는 notification_id/action_url 이 없으면
    // "계약 위반" 으로 폐기한다.
    // 하위호환 path 는 계약 키 action_url 과 **같은 값**이어야 한다. 둘이 갈리면 네이티브가
    // 어느 키를 읽느냐에 따라 목적지가 달라진다.
    const payload = { action: PUSH_ROUTE_ACK_ACTION, status, path: actionUrl };
    if (notificationId !== null) {
        payload.pushId = notificationId;
        payload.notification_id = notificationId;
    }
    // 경로를 만들지 못한 invalid 는 실을 action_url 이 없다. 자리를 채우려고 "/" 같은 값을
    // 지어내면 네이티브 로그가 이동하지도 않은 목적지를 가리키므로 키를 생략한다.
    if (actionUrl !== null) payload.action_url = actionUrl;

    try {
        // terminal 신호라 중복 발신이 곧 조기 소비다. 한 채널로만 보낸다.
        if (window.Android?.pushRouteAck) {
            window.Android.pushRouteAck(JSON.stringify(payload));
        } else if (window.nativeApp?.pushRouteAck) {
            window.nativeApp.pushRouteAck(JSON.stringify(payload));
        } else if (window.webkit?.messageHandlers?.nativeApp) {
            window.webkit.messageHandlers.nativeApp.postMessage({
                action: "pushRouteAck",
                ...payload,
            });
        }
    } catch (e) {
        // native bridge 실패는 무시
    }
};

const ACCESS_TOKEN_STORAGE_KEYS = [
    "accessToken",
    "access_token",
];

const normalizeAccessToken = (token) => {
    if (!token || typeof token !== "string") return null;
    const trimmed = token.trim();
    if (!trimmed) return null;
    return trimmed.replace(/^Bearer\s+/i, "");
};

const getStoredAccessToken = () => {
    for (const key of ACCESS_TOKEN_STORAGE_KEYS) {
        const token = normalizeAccessToken(localStorage.getItem(key));
        if (token) return token;
    }

    for (const key of ACCESS_TOKEN_STORAGE_KEYS) {
        const token = normalizeAccessToken(sessionStorage.getItem(key));
        if (token) return token;
    }

    return null;
};

const applyAuthHeaders = async (config) => {
    config.headers = config.headers || {};

    const token = TokenManager.getAccessToken();
    if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (isNative()) {
        config.headers["X-Client-Type"] = "app";
    }
    const refreshToken = await TokenManager.getRefreshToken();
    if (refreshToken) {
        config.headers["X-Refresh-Token"] = refreshToken;
    }
    return config;
};

// 토큰 관리 유틸리티
// 액세스 토큰은 localStorage 에만 쓰여서 React 재렌더를 유발하지 않는다.
// 네이티브가 window.onAuthToken 으로 토큰만 복원하는 경로가 있으므로,
// 토큰 변화에 반응해야 하는 쪽(딥링크 pending 소비 등)이 구독할 수 있게 이벤트를 쏜다.
const ACCESS_TOKEN_CHANGE_EVENT = "buccl:access-token-changed";

const notifyAccessTokenChange = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(ACCESS_TOKEN_CHANGE_EVENT));
};

const TokenManager = {
    saveAccessToken(accessToken) {
        const token = normalizeAccessToken(accessToken);
        if (!token) return;
        // 같은 토큰의 재저장(리프레시 응답이 동일 토큰을 돌려주는 경우 등)은 상태 변화가 아니다.
        // 그대로 발행하면 구독자(useDeepLink)가 매번 tick 을 올려 App 이 헛돌게 된다.
        const previousToken = getStoredAccessToken();
        localStorage.setItem("accessToken", token);
        if (previousToken !== token) notifyAccessTokenChange();
    },

    getAccessToken() {
        return getStoredAccessToken();
    },

    async saveRefreshToken(refreshToken) {
        if (!refreshToken) return;
        // App WebView: native owns the refresh token — do not persist in FE
        if (isAppWebView()) return;
        sessionStorage.setItem("refreshToken", refreshToken);
    },

    async getRefreshToken() {
        // App WebView: native owns the refresh token
        if (isAppWebView()) return null;
        return sessionStorage.getItem("refreshToken");
    },

    async clearRefreshToken() {
        sessionStorage.removeItem("refreshToken");
    },

    async clearTokens() {
        // 이미 토큰이 없던 상태에서의 정리(비로그인 401, 중복 로그아웃)는 상태 변화가 아니다.
        const hadAccessToken = Boolean(getStoredAccessToken());
        ACCESS_TOKEN_STORAGE_KEYS.forEach((key) => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        if (hadAccessToken) notifyAccessTokenChange();
        await this.clearRefreshToken();
    },

    // 토큰 만료 체크 (JWT 토큰의 경우)
    isTokenExpired(token) {
        if (!token) return true;
        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp < currentTime;
        } catch (error) {
            return true;
        }
    },
};

// Native secure storage 정리
const notifyNativeLogout = () => {
    try {
        if (window?.Android?.logout) {
            window.Android.logout();
        }
        if (window?.nativeApp?.logout) {
            window.nativeApp.logout();
        }
        if (window?.webkit?.messageHandlers?.nativeApp) {
            window.webkit.messageHandlers.nativeApp.postMessage({ action: "logout" });
        }
    } catch (e) {
        // native bridge 실패는 무시
    }
};

// 강제 로그아웃 함수
const forceLogout = async () => {
    try {
        notifyNativeLogout();

        // 토큰 완전 제거 (access + refresh)
        await TokenManager.clearTokens();

        // 로컬 스토리지의 사용자 관련 데이터 모두 제거
        localStorage.removeItem("userInfoStorage");

        // Zustand 스토어 상태 초기화
        const userStore = useUserStore.getState();
        if (userStore && typeof userStore.clearUserInfo === "function") {
            userStore.clearUserInfo();
        }

        // 페이지 이동 전에 잠시 대기하여 상태 정리 완료 보장
        setTimeout(() => {
            window.location.href = "/login";
        }, 100);
    } catch (error) {
        if (process.env.NODE_ENV !== "production") {
            console.error("로그아웃 처리 중 오류:", error);
        }
        window.location.href = "/";
    }
};

// --- Native 401 recovery ---
let _recoveryPromise = null;

const requestRecovery = () => {
    if (!isAppWebView()) return Promise.reject(new Error("not_app_webview"));

    // Dedupe: concurrent 401s share one recoveryPromise
    if (_recoveryPromise) return _recoveryPromise;

    _recoveryPromise = new Promise((resolve, reject) => {
        const requestId = crypto.randomUUID();

        // 10s timeout
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error("recovery_timeout"));
        }, 10000);

        const cleanup = () => {
            clearTimeout(timer);
            window.onAuthSessionRecovered = _originalOnAuthSessionRecovered;
            _recoveryPromise = null;
        };

        // Save original callback if any
        const _originalOnAuthSessionRecovered = window.onAuthSessionRecovered;

        window.onAuthSessionRecovered = ({ requestId: rid, success, accessToken, reason }) => {
            if (rid !== requestId) return;
            cleanup();
            if (success && accessToken) {
                TokenManager.saveAccessToken(accessToken);
                resolve(accessToken);
            } else {
                reject(new Error(reason || "recovery_failed"));
            }
        };

        // Call native bridge
        try {
            if (window.Android?.recoverAuthSession) {
                window.Android.recoverAuthSession(requestId);
            } else if (window.nativeApp?.recoverAuthSession) {
                window.nativeApp.recoverAuthSession(requestId);
            } else if (window.webkit?.messageHandlers?.nativeApp) {
                window.webkit.messageHandlers.nativeApp.postMessage({
                    action: "recoverAuthSession",
                    requestId,
                });
            } else {
                cleanup();
                reject(new Error("no_native_bridge"));
            }
        } catch (err) {
            cleanup();
            reject(err);
        }
    });

    return _recoveryPromise;
};

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true,
});

api.interceptors.request.use(
    applyAuthHeaders,
    (error) => {
        return Promise.reject(error);
    }
);

// 백엔드 자동 갱신 로직과 연동된 응답 인터셉터
api.interceptors.response.use(
    async (response) => {
        const isTokenRefreshed =
            response.headers["x-token-refreshed"] ||
            response.headers["X-Token-Refreshed"];
        const newAccessToken =
            response.headers["x-new-access-token"] ||
            response.headers["X-New-Access-Token"];
        const newRefreshToken =
            response.headers["x-new-refresh-token"] ||
            response.headers["X-New-Refresh-Token"];
        const normalizedRefreshFlag = String(isTokenRefreshed ?? "").toLowerCase();
        const shouldUpdateToken =
            normalizedRefreshFlag === "true" || normalizedRefreshFlag === "1";

        if (shouldUpdateToken && newAccessToken) {
            TokenManager.saveAccessToken(newAccessToken);
            if (isAppWebView()) {
                syncNativeAccessToken(newAccessToken);
                syncNativeRefreshCookie();
            }
            if (process.env.NODE_ENV !== "production") {
                console.log("✅ 백엔드에서 자동 갱신된 토큰을 저장했습니다.");
            }
        }

        if (newRefreshToken) {
            await TokenManager.saveRefreshToken(newRefreshToken);
        }

        return response;
    },
    async (error) => {
        if (!error.response) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        if (error.response?.status === 401) {
            // App WebView: attempt native recovery before logout
            if (isAppWebView() && !originalRequest._recoveryAttempted) {
                originalRequest._recoveryAttempted = true;
                try {
                    const newAccessToken = await requestRecovery();
                    originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
                    return api(originalRequest);
                } catch {
                    forceLogout();
                    return Promise.reject(error);
                }
            }

            // Web environment or recovery already failed
            if (process.env.NODE_ENV !== "production") {
                console.log("❌ 인증 오류가 발생했습니다. 로그인 페이지로 이동합니다.");
            }
            forceLogout();
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

const attachAuthInterceptors = (client) => {
    client.interceptors.request.use(
        applyAuthHeaders,
        (error) => Promise.reject(error)
    );

    client.interceptors.response.use(
        async (response) => {
            const isTokenRefreshed =
                response.headers["x-token-refreshed"] ||
                response.headers["X-Token-Refreshed"];
            const newAccessToken =
                response.headers["x-new-access-token"] ||
                response.headers["X-New-Access-Token"];
            const newRefreshToken =
                response.headers["x-new-refresh-token"] ||
                response.headers["X-New-Refresh-Token"];
            const normalizedRefreshFlag = String(isTokenRefreshed ?? "").toLowerCase();
            const shouldUpdateToken =
                normalizedRefreshFlag === "true" || normalizedRefreshFlag === "1";

            if (shouldUpdateToken && newAccessToken) {
                TokenManager.saveAccessToken(newAccessToken);
                if (isAppWebView()) {
                    syncNativeAccessToken(newAccessToken);
                    syncNativeRefreshCookie();
                }
            }

            if (newRefreshToken) {
                await TokenManager.saveRefreshToken(newRefreshToken);
            }

            return response;
        },
        async (error) => {
            if (!error.response) {
                return Promise.reject(error);
            }

            const originalRequest = error.config;

            if (error.response?.status === 401) {
                // App WebView: attempt native recovery before logout
                if (isAppWebView() && !originalRequest._recoveryAttempted) {
                    originalRequest._recoveryAttempted = true;
                    try {
                        const newAccessToken = await requestRecovery();
                        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
                        return client(originalRequest);
                    } catch {
                        forceLogout();
                        return Promise.reject(error);
                    }
                }

                forceLogout();
                return Promise.reject(error);
            }

            return Promise.reject(error);
        }
    );

    return client;
};

// Community API
const communityApi = attachAuthInterceptors(axios.create({
    baseURL: process.env.REACT_APP_COMMUNITY_API_URL || undefined,
    withCredentials: true,
}));

// Chat MSA API
const chatApi = attachAuthInterceptors(axios.create({
    baseURL: process.env.REACT_APP_CHAT_API_URL || undefined,
    withCredentials: true,
}));

export {
    ACCESS_TOKEN_CHANGE_EVENT,
    chatApi,
    communityApi,
    TokenManager,
    isAppWebView,
    requestRecovery,
    notifyNativeLogout,
    sendPushRouteAck,
    syncNativeAccessToken,
    syncNativeRefreshCookie,
};
export default api;
